
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { ScheduledPickup, ScheduledPickupsWidget } from '@/features/dashboard/types';
import Content from '@/features/dashboard/components/widgets/scheduledPickups/Content.vue';
import DataRowSkeleton from '@/features/dashboard/components/widgets/DataRowSkeleton.vue';
import { getScheduledPickupsData } from '@/features/dashboard/services/dashboard.services';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { WidgetMixin } from '../widgetMixin';

@Component({
  components: {
    Content,
    DataRowSkeleton,
  },
})
export default class ScheduledPickups extends Mixins(WidgetMixin) {
  private pickupsShown = 10;

  private pickupsShownItems = Array.from({ length: 25 }, (_, i) => i + 1)

  get scheduledPickups(): Array<ScheduledPickup> {
    return this.$store.getters.getScheduledPickups || null;
  }

  get isDashboardSettingsLoaded(): boolean {
    return this.$store.getters.getIsDashboardSettings;
  }

  get widget(): ScheduledPickupsWidget {
    return this.$store.getters.getWidgets.scheduledPickups;
  }

  private isLoading = true;

  private resizeObserver: ResizeObserver | null = null;

  mounted(): void {
    this.setupResizeObserver();
  }

  beforeDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  @Watch('isDashboardSettingsLoaded', { immediate: true })
  private loadData(val) {
    if (!val) return;

    if (this.widget.itemsShown && this.widget.itemsShown <= 25) {
      this.pickupsShown = this.widget.itemsShown;
    } else {
      this.pickupsShown = 10;
    }

    this.updateWidgetData();
  }

  private async updateWidgetData(): Promise<void> {
    const data = await getScheduledPickupsData(this.pickupsShown, 0);
    this.$store.commit('setScheduledPickups', data);
    this.isLoading = false;
    this.rerenderWidget();
    this.$nextTick(() => {
      this.setupResizeObserver();
    });
    eventBus.$emit(eventNames.DASHBOARD_WIDGET_DATA_LOADED);
  }

  private setupResizeObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.$nextTick(() => {
      const widgetWrap = this.$refs.widgetWrap as any;
      if (!widgetWrap) return;

      const widgetFront = (widgetWrap as any).widgetFront as HTMLElement;
      const widgetWrapEl = (widgetWrap as any).widgetWrap as HTMLElement;

      if (!widgetFront || !widgetWrapEl) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.updateWidgetHeight(widgetFront, widgetWrapEl);
      });

      this.resizeObserver.observe(widgetFront);
      this.updateWidgetHeight(widgetFront, widgetWrapEl);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private updateWidgetHeight(widgetFront: HTMLElement, widgetWrapEl: HTMLElement): void {
    const frontElement = widgetFront;
    const wrapElement = widgetWrapEl;

    frontElement.style.height = '';
    const contentHeight = frontElement.scrollHeight;
    const minHeightStr = getComputedStyle(wrapElement).minHeight;
    const minHeight = minHeightStr ? +minHeightStr.replace('px', '') : 0;
    const targetHeight = Math.max(contentHeight, minHeight);

    if (targetHeight > 0) {
      const originalTransition = wrapElement.style.transition;
      wrapElement.style.transition = 'none';
      wrapElement.style.height = `${targetHeight}px`;
      // Force browser reflow to apply height immediately
      // eslint-disable-next-line no-unused-expressions
      wrapElement.offsetHeight;

      if (originalTransition) {
        wrapElement.style.transition = originalTransition;
      } else {
        wrapElement.style.removeProperty('transition');
      }
    }
  }

  private handleApply(widgetName: string) {
    this.apply('big');

    this.$store.dispatch('syncDashboardSetting', {
      dashboardWidget: widgetName,
      visible: true,
      position: this.currentPosition,
      itemsShown: this.pickupsShown,
      customSettings: null,
    });

    if (this.position !== this.currentPosition) {
      this.$store.dispatch('patchDashboardSetting', { size: 'big' });
      eventBus.$emit(eventNames.SORT_DASHBOARD_WIDGETS_POSITION, 'big');
    }

    this.updateWidgetData();
  }
}
